/**
 * Curve.js — the cumulative cash curve
 * ====================================
 * Money out of the fund into the deal, money back. The deepest point is the
 * most cash the deal ever had tied up at once, which is less than the sum of
 * its outflows on a deal that recovered money and redeployed it — and the
 * difference is the whole point of the number.
 *
 * Two callers: one deal, and a whole strategy added together. The deal's
 * points come out of its own series; the strategy's come out of the group
 * rollup the API built, and neither knows about the other.
 */
import React, { useMemo } from "react";
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceDot,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip as ChartTooltip,
  XAxis,
  YAxis,
} from "recharts";
import { EmptyState } from "../../components/ui";
import { compactMoney } from "../../components/format";
import theme from "../../components/theme";
import { day, dealMoney, viewOf } from "./figures";
import { readerText } from "./text";
import { showFundingRounds } from "./FundingRounds";

const tooltipBox = {
  background: theme.bgCard,
  border: `1px solid ${theme.border}`,
  borderRadius: theme.radius,
  boxShadow: theme.shadowMd,
  padding: "10px 12px",
  fontSize: 12,
  maxWidth: 320,
  fontVariantNumeric: "tabular-nums",
};

function CurveTooltip({ active, payload, currency, dealName }) {
  if (!active || !payload?.length) return null;
  const point = payload[0].payload;
  return (
    <div style={tooltipBox}>
      <div style={{ fontWeight: 600, marginBottom: 6 }}>{day(point.date)}</div>
      <div>Cumulative {dealMoney(point.cumulative, currency)}</div>
      <div style={{ color: point.flow < 0 ? theme.danger : theme.success }}>
        {point.flow < 0 ? "Deployed " : "Recovered "}
        {dealMoney(Math.abs(point.flow), currency)}
      </div>
      {point.events?.slice(0, 4).map((event, i) => (
        <div key={i} style={{ color: theme.textSecondary, marginTop: 4 }}>
          {event.account}
          {event.description ? ` — ${readerText(event.description, { dealName }).slice(0, 70)}` : ""}
        </div>
      ))}
      {point.events?.length > 4 && (
        <div style={{ color: theme.textMuted, marginTop: 4 }}>+ {point.events.length - 4} more</div>
      )}
    </div>
  );
}

/**
 * Round ticks for the cash axis, with nil always one of them. Left to itself
 * the axis put its ticks on the padded domain's own fractions — $4.4M, $2.3M,
 * ($1.7M) — and the line a deal breaks even on had no label at all.
 */
export function cashTicks(values, count = 5) {
  const lo = Math.min(0, ...values);
  const hi = Math.max(0, ...values);
  const span = hi - lo || Math.abs(hi) || 1;
  const raw = span / count;
  const power = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * power).find((v) => v >= raw) || 10 * power;
  const first = Math.floor(lo / step) * step;
  const last = Math.ceil(hi / step) * step;
  const ticks = [];
  // `|| 0` turns a -0 into the nil it is, so the axis never prints "(0)".
  for (let v = first; v <= last + step / 2; v += step) ticks.push(Math.round(v / step) * step || 0);
  return ticks;
}

// `markers` are dated vertical lines — a deal's tranche fundings, labelled
// T1, T2 — so the cliff on the curve can be read against the Tranches tab.
export function Curve({ points, troughDate, tooltip, empty, markers = [], currency = "USD" }) {
  if (!points.length) {
    return <EmptyState message={empty} icon="◌" />;
  }

  const trough = points.find((p) => p.date === troughDate);
  const axis = (v) => compactMoney(v, { currency: /^[A-Z]{3}$/.test(currency) ? currency : "USD" });
  const ticks = cashTicks(points.map((p) => p.cumulative));

  return (
    <div style={{ width: "100%", height: 320 }}>
      <ResponsiveContainer>
        <ComposedChart data={points} margin={{ top: 8, right: 16, bottom: 4, left: 8 }}>
          <defs>
            <linearGradient id="cashFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={theme.accent} stopOpacity={0.22} />
              <stop offset="100%" stopColor={theme.accent} stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <CartesianGrid stroke={theme.borderLight} strokeDasharray="3 3" />
          <XAxis
            dataKey="date"
            tickFormatter={day}
            tick={{ fontSize: 11, fill: theme.textMuted }}
            stroke={theme.border}
            minTickGap={36}
          />
          {/* Zero has to be in view. Without it the axis starts at the deepest
              point, the fill covers the whole panel, and a deal that never came
              close to breaking even looks like one that did. */}
          <YAxis
            tickFormatter={axis}
            tick={{ fontSize: 11, fill: theme.textMuted }}
            stroke={theme.border}
            width={72}
            domain={[ticks[0], ticks[ticks.length - 1]]}
            ticks={ticks}
          />
          <ReferenceLine y={0} stroke={theme.textMuted} strokeWidth={1} />
          {markers.map((m) => (
            <ReferenceLine
              key={m.date}
              x={m.date}
              stroke={theme.accent}
              strokeDasharray="4 3"
              strokeOpacity={0.7}
              label={{ value: m.label, position: "insideTopLeft", fill: theme.textSecondary, fontSize: 11, fontWeight: 600 }}
            />
          ))}
          <ChartTooltip content={tooltip} />
          <Area type="stepAfter" dataKey="cumulative" baseValue={0} stroke="none" fill="url(#cashFill)" isAnimationActive={false} />
          <Line
            type="stepAfter"
            dataKey="cumulative"
            stroke={theme.accent}
            strokeWidth={2}
            dot={points.length <= 40 ? { r: 2, fill: theme.accent } : false}
            isAnimationActive={false}
          />
          {trough && (
            <ReferenceDot x={trough.date} y={trough.cumulative} r={4} fill={theme.danger} stroke={theme.bgCard} strokeWidth={2} />
          )}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

// The chart's T1, T2 lines: the same fundings the Tranches tab splits on, and
// only where that tab has a split to show, so a line on the chart always has
// its explanation one click away. An equity tranche is E1, E2 — Brian, on
// Hilbert Group's: "make it obvious what it is, its not another note".
export function fundingMarkers(deal) {
  if (!showFundingRounds(deal)) return [];
  return deal.funding_rounds.fundings.map((f) => ({ date: f.date, label: f.short }));
}

export function CashCurve({ deal }) {
  const { currency, summary, converted } = viewOf(deal);
  const points = useMemo(() => {
    // On a converted deal the cumulative line has to be re-accumulated here:
    // the API sends `cash_flow_usd` per day but carries `cumulative_cash` only
    // in the deal's own mixed currencies, which is the sum this exists to avoid.
    let running = 0;
    return (deal.series || []).map((p) => {
      const flow = Number(converted ? p.cash_flow_usd : p.cash_flow);
      running += flow;
      return {
        date: p.date,
        cumulative: converted ? running : Number(p.cumulative_cash),
        flow,
        events: p.events,
      };
    });
  }, [deal, converted]);

  const markers = useMemo(() => fundingMarkers(deal), [deal]);

  return (
    <Curve
      points={points}
      troughDate={summary.max_cash_exposure_date}
      tooltip={<CurveTooltip currency={currency} dealName={deal.deal.name} />}
      empty="No cash movement is posted for this deal yet."
      markers={markers}
      currency={currency}
    />
  );
}

function StrategyTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const point = payload[0].payload;
  return (
    <div style={tooltipBox}>
      <div style={{ fontWeight: 600, marginBottom: 6 }}>{day(point.date)}</div>
      <div>Cumulative {dealMoney(point.cumulative, "USD")}</div>
      <div style={{ color: point.flow < 0 ? theme.danger : theme.success }}>
        {point.flow < 0 ? "Deployed " : "Recovered "}
        {dealMoney(Math.abs(point.flow), "USD")}
      </div>
      {/* Which deals made the step, named. A step nobody can read back to a
          deal is a number with no way to check it. */}
      {point.deals?.slice(0, 5).map((d, i) => (
        <div key={i} style={{ color: theme.textSecondary, marginTop: 4 }}>
          {d.name} {dealMoney(d.amount, "USD")}
        </div>
      ))}
      {point.deals?.length > 5 && (
        <div style={{ color: theme.textMuted, marginTop: 4 }}>+ {point.deals.length - 5} more</div>
      )}
    </div>
  );
}

export function StrategyCurve({ group }) {
  const points = useMemo(
    () =>
      (group.series || []).map((p) => ({
        date: p.date,
        cumulative: Number(p.cumulative_cash),
        flow: Number(p.cash_flow),
        deals: (p.deals || []).map((d) => ({ name: d.deal, amount: Number(d.amount) })),
      })),
    [group]
  );

  return (
    <Curve
      points={points}
      troughDate={group.summary.max_cash_exposure_date}
      tooltip={<StrategyTooltip />}
      empty="No deal of this kind has posted cash movement yet."
    />
  );
}
